// coffee.js — bean-aware coffee dose & grind calculator.
// Tuned specifically for Fellow Aiden brewer + Fellow Ode Gen 2 grinder.
//
// Produces a *full* Aiden profile object — every parameter the brewer's
// settings expose (bloom ratio/duration/temp, single-serve and batch pulse
// counts/intervals/per-pulse temps). The shape matches what the
// reverse-engineered Fellow API expects, so the same object can be rendered
// in the UI today and pushed to the brewer later via aiden.js.

export const ROAST_LEVELS = [
  { id: "light", label: "Light", color: "#D4B896" },
  { id: "medium-light", label: "Medium-Light", color: "#B8956A" },
  { id: "medium", label: "Medium", color: "#8B6F47" },
  { id: "medium-dark", label: "Medium-Dark", color: "#5C4530" },
  { id: "dark", label: "Dark", color: "#3D2D1F" },
];

export const PROCESS_TYPES = [
  { id: "washed", label: "Washed", note: "Clean acidity, balanced" },
  { id: "natural", label: "Natural", note: "Fruity, sometimes funky" },
  { id: "honey", label: "Honey", note: "Sweet, balanced" },
  { id: "anaerobic", label: "Anaerobic", note: "Intense, often funky" },
  { id: "wet-hulled", label: "Wet-Hulled", note: "Earthy, full-bodied" },
];

// Aiden brew profiles. Each carries the *full* parameter set the brewer accepts.
// profileType: 0 = pour-over (hot), 1 = cold brew.
// Per-pulse temperature arrays let you ramp temps across pulses; length must
// match pulse count. Single-value arrays hold for every pulse.
export const AIDEN_PROFILES = {
  "fellows-classic": {
    label: "Fellow's Classic",
    description: "Balanced, the default starting point",
    profileType: 0,
    ratio: 16.5,
    bloomEnabled: true,
    bloomRatio: 2.5,
    bloomDuration: 45,
    bloomTemperature: 96,
    ssPulsesEnabled: true,
    ssPulsesNumber: 3,
    ssPulsesInterval: 23,
    ssPulseTemperatures: [96, 96, 96],
    batchPulsesEnabled: true,
    batchPulsesNumber: 3,
    batchPulsesInterval: 30,
    batchPulseTemperatures: [96, 96, 96],
  },
  "bold": {
    label: "Bold",
    description: "Heavier body, more concentration",
    profileType: 0,
    ratio: 15,
    bloomEnabled: true,
    bloomRatio: 2,
    bloomDuration: 45,
    bloomTemperature: 96,
    ssPulsesEnabled: true,
    ssPulsesNumber: 3,
    ssPulsesInterval: 25,
    ssPulseTemperatures: [96, 96, 96],
    batchPulsesEnabled: true,
    batchPulsesNumber: 3,
    batchPulsesInterval: 32,
    batchPulseTemperatures: [96, 96, 96],
  },
  "balanced": {
    label: "Balanced",
    description: "Wide-window everyday brew",
    profileType: 0,
    ratio: 16,
    bloomEnabled: true,
    bloomRatio: 2.5,
    bloomDuration: 40,
    bloomTemperature: 95,
    ssPulsesEnabled: true,
    ssPulsesNumber: 3,
    ssPulsesInterval: 22,
    ssPulseTemperatures: [95, 95, 95],
    batchPulsesEnabled: true,
    batchPulsesNumber: 3,
    batchPulsesInterval: 28,
    batchPulseTemperatures: [95, 95, 95],
  },
  "light-roast": {
    label: "Light Roast Special",
    description: "Higher temp, longer bloom, ramped pulses for clarity",
    profileType: 0,
    ratio: 16.5,
    bloomEnabled: true,
    bloomRatio: 3,
    bloomDuration: 60,
    bloomTemperature: 98,
    ssPulsesEnabled: true,
    ssPulsesNumber: 4,
    ssPulsesInterval: 22,
    ssPulseTemperatures: [96, 97, 98, 98],
    batchPulsesEnabled: true,
    batchPulsesNumber: 4,
    batchPulsesInterval: 28,
    batchPulseTemperatures: [96, 97, 98, 98],
  },
  "iced": {
    label: "Iced (Japanese-style)",
    description: "Brewed hot over ice; uses less water",
    profileType: 0,
    ratio: 11,
    bloomEnabled: true,
    bloomRatio: 2.5,
    bloomDuration: 45,
    bloomTemperature: 96,
    ssPulsesEnabled: true,
    ssPulsesNumber: 3,
    ssPulsesInterval: 23,
    ssPulseTemperatures: [96, 96, 96],
    batchPulsesEnabled: true,
    batchPulsesNumber: 3,
    batchPulsesInterval: 30,
    batchPulseTemperatures: [96, 96, 96],
    note: "Add ~40% of yield as ice in carafe before brewing",
  },
  "cold-brew": {
    label: "Cold Brew",
    description: "Long slow extraction, no heat",
    profileType: 1,
    ratio: 8,
    bloomEnabled: false,
    bloomRatio: 0,
    bloomDuration: 0,
    bloomTemperature: 0,
    ssPulsesEnabled: false,
    ssPulsesNumber: 0,
    ssPulsesInterval: 0,
    ssPulseTemperatures: [],
    batchPulsesEnabled: false,
    batchPulsesNumber: 0,
    batchPulsesInterval: 0,
    batchPulseTemperatures: [],
    note: "Cold brew runs the brewer's automated cold mode",
  },
};

// Ode Gen 2 grind setting recommendations by brew style.
// 1 = finest, 11 = coarsest (Ode Gen 2 numbered detents).
// Canonical Ode Gen 2 settings from Josh's handoff: hot brews live ~5.3–5.6,
// cold brew ~8.2 (coarser). (Superseded the stale ~8.1 "favorite".)
export const ODE_GRIND_GUIDE = {
  "fellows-classic": { setting_range: [4, 6], default: 5, descriptor: "medium-fine" },
  "bold":            { setting_range: [3, 5], default: 4, descriptor: "medium-fine" },
  "balanced":        { setting_range: [4, 6], default: 5, descriptor: "medium-fine" },
  "light-roast":     { setting_range: [3, 5], default: 4, descriptor: "medium-fine" },
  "iced":            { setting_range: [4, 6], default: 5, descriptor: "medium-fine" },
  "cold-brew":       { setting_range: [7, 9], default: 8, descriptor: "coarse" },
};

const ROAST_GRIND_OFFSET  = { "light": -1,   "medium-light": -0.5, "medium": 0, "medium-dark": +0.5, "dark": +1 };
const ROAST_TEMP_OFFSET   = { "light": +2,   "medium-light": +1,   "medium": 0, "medium-dark": -1,   "dark": -2 };
const ROAST_RATIO_OFFSET  = { "light": +0.5, "medium-light": +0.25,"medium": 0, "medium-dark": -0.25,"dark": -0.5 };

/**
 * Compute the full Aiden brew object for a given bean + base profile + water target.
 * Returns a denormalized result with everything the UI needs to display, plus an
 * `aidenProfile` field shaped exactly like the Fellow API's profile schema.
 */
export function calculateAidenBrew({ bean, profileId = "balanced", waterMl = 500, profileTitle = null, ratioOverride = null }) {
  const base = AIDEN_PROFILES[profileId] || AIDEN_PROFILES.balanced;
  const roast = bean?.roast_level || "medium";

  // ratioOverride lets the user log a one-off brew at a custom ratio without
  // creating a new Aiden profile — bypasses both the profile's base ratio
  // and the roast-bias offset.
  const ratio = ratioOverride && ratioOverride > 0
    ? +(+ratioOverride).toFixed(2)
    : +(base.ratio + (ROAST_RATIO_OFFSET[roast] || 0)).toFixed(2);
  const tempOffset = ROAST_TEMP_OFFSET[roast] || 0;

  const dose = +(waterMl / ratio).toFixed(1);
  const bloomWater = +(dose * base.bloomRatio).toFixed(0);

  const bloomTemp = base.bloomEnabled ? clampTemp(base.bloomTemperature + tempOffset) : 0;
  const ssPulseTemps    = base.ssPulseTemperatures.map(t => clampTemp(t + tempOffset));
  const batchPulseTemps = base.batchPulseTemperatures.map(t => clampTemp(t + tempOffset));

  const grind = computeOdeSetting(profileId, roast);
  const freshness = freshnessNote(bean?.roast_date);
  const processHint = processHints[bean?.process] || null;

  const displayTempC = ssPulseTemps.length
    ? Math.max(...ssPulseTemps)
    : (bloomTemp || base.bloomTemperature || 0);

  // The Aiden profile object — matches the Fellow API schema 1:1.
  const aidenProfile = {
    profileType: base.profileType,
    title: profileTitle || synthProfileTitle(bean, profileId),
    ratio,
    bloomEnabled: base.bloomEnabled,
    bloomRatio: base.bloomRatio,
    bloomDuration: base.bloomDuration,
    bloomTemperature: bloomTemp,
    ssPulsesEnabled: base.ssPulsesEnabled,
    ssPulsesNumber: base.ssPulsesNumber,
    ssPulsesInterval: base.ssPulsesInterval,
    ssPulseTemperatures: ssPulseTemps,
    batchPulsesEnabled: base.batchPulsesEnabled,
    batchPulsesNumber: base.batchPulsesNumber,
    batchPulsesInterval: base.batchPulsesInterval,
    batchPulseTemperatures: batchPulseTemps,
  };

  return {
    bean_id: bean?.id || null,
    bean_name: bean?.name || "Unknown bean",
    profile: { id: profileId, label: base.label, description: base.description },
    dose_g: dose,
    water_g: waterMl,
    bloom_water_g: bloomWater,
    bloom_time_s: base.bloomDuration,
    ratio,
    ratio_str: `1:${ratio.toFixed(1)}`,
    temp_c: displayTempC,
    temp_f: +(displayTempC * 9 / 5 + 32).toFixed(0),
    pulse_count: base.ssPulsesNumber,
    grind: {
      device: "Fellow Ode Gen 2",
      setting: grind.setting,
      setting_str: `Setting ${grind.setting}`,
      descriptor: grind.descriptor,
      adjustment_note: grind.adjustment,
    },
    freshness,
    process_hint: processHint,
    note: base.note || null,
    aidenProfile,
  };
}

function clampTemp(t) {
  if (t == null) return 0;
  return Math.max(50, Math.min(99, Math.round(t)));
}

function synthProfileTitle(bean, profileId) {
  const base = AIDEN_PROFILES[profileId];
  const beanName = bean?.name || "Custom";
  const synth = `${beanName} · ${base?.label || profileId}`;
  return synth.length > 30 ? synth.slice(0, 30) : synth;
}

function computeOdeSetting(profileId, roast) {
  const guide = ODE_GRIND_GUIDE[profileId] || ODE_GRIND_GUIDE.balanced;
  const offset = ROAST_GRIND_OFFSET[roast] || 0;
  const setting = Math.max(1, Math.min(11, Math.round(guide.default + offset)));
  let adjustment = "";
  if (offset < 0) adjustment = `Lighter roast — ${Math.abs(offset)} click finer than default`;
  else if (offset > 0) adjustment = `Darker roast — ${offset} click coarser than default`;
  else adjustment = `Default for medium roast`;
  return { setting, descriptor: guide.descriptor, adjustment };
}

function freshnessNote(roastDate) {
  if (!roastDate) return { status: "unknown", text: "No roast date — can't assess freshness" };
  const days = Math.floor((Date.now() - new Date(roastDate).getTime()) / 86400000);
  if (days < 3)  return { status: "too-fresh", days, text: `${days}d off roast — too fresh, rest 1-2 more days` };
  if (days <= 21) return { status: "peak",     days, text: `${days}d off roast — peak window` };
  if (days <= 35) return { status: "past-peak",days, text: `${days}d off roast — past peak, may need finer grind` };
  return            { status: "stale",     days, text: `${days}d off roast — stale, flavors will be flat` };
}

const processHints = {
  washed: "Clean acidity. Highlights the bean's terroir.",
  natural: "Fruit-forward, sometimes boozy. Try the lighter ratio if too intense.",
  anaerobic: "Funky and intense. Don't be alarmed by unusual flavors.",
  honey: "Balanced sweetness, very forgiving across settings.",
  "wet-hulled": "Earthy and full-bodied. Try the bold profile.",
};

// ---------------------------------------------------------------------------
// User-edited profile overrides
// ---------------------------------------------------------------------------
// Apply user edits on top of the computed profile. The editor passes a partial
// aidenProfile and we merge — recomputing dose/bloom water from ratio + waterMl.
export function applyProfileOverrides(brew, overrides) {
  if (!overrides || !brew) return brew;
  const merged = { ...brew.aidenProfile, ...overrides };
  // Keep pulse temperature arrays in sync with their pulse-count counterpart.
  if (overrides.ssPulsesNumber != null) {
    merged.ssPulseTemperatures = padOrTrim(merged.ssPulseTemperatures, overrides.ssPulsesNumber);
  }
  if (overrides.batchPulsesNumber != null) {
    merged.batchPulseTemperatures = padOrTrim(merged.batchPulseTemperatures, overrides.batchPulsesNumber);
  }

  const dose = +(brew.water_g / merged.ratio).toFixed(1);
  const bloomWater = +(dose * merged.bloomRatio).toFixed(0);
  const headlineTemp = merged.ssPulseTemperatures?.length
    ? Math.max(...merged.ssPulseTemperatures)
    : merged.bloomTemperature;

  return {
    ...brew,
    aidenProfile: merged,
    dose_g: dose,
    bloom_water_g: bloomWater,
    ratio: merged.ratio,
    ratio_str: `1:${merged.ratio.toFixed(1)}`,
    temp_c: headlineTemp,
    temp_f: +(headlineTemp * 9 / 5 + 32).toFixed(0),
    pulse_count: merged.ssPulsesNumber,
    bloom_time_s: merged.bloomDuration,
  };
}

function padOrTrim(arr, len) {
  const a = arr?.slice() || [];
  if (a.length === len) return a;
  if (a.length > len) return a.slice(0, len);
  const last = a[a.length - 1] ?? 96;
  while (a.length < len) a.push(last);
  return a;
}

// Bounded inputs the editor uses to constrain sliders.
export const AIDEN_LIMITS = {
  ratio: { min: 10, max: 22, step: 0.5 },
  bloomRatio: { min: 1, max: 4, step: 0.5 },
  bloomDuration: { min: 0, max: 120, step: 5 },
  bloomTemperature: { min: 50, max: 99, step: 1 },
  pulsesNumber: { min: 1, max: 6, step: 1 },
  pulsesInterval: { min: 5, max: 60, step: 1 },
  pulseTemperature: { min: 50, max: 99, step: 1 },
  waterMl: { min: 150, max: 1500, step: 10 },
};

// Dial-in feedback: given a previous brew + tasting feedback, suggest one change.
export function suggestAdjustment(previousBrew, feedback) {
  const f = (feedback || "").toLowerCase();
  const adjustments = [];

  if (f.includes("sour") || f.includes("under") || f.includes("acidic")) {
    adjustments.push({
      lever: "grind", direction: "finer",
      change: "Move grind 1 click finer",
      newSetting: Math.max(1, previousBrew.grind.setting - 1),
      reason: "Sour/underextracted — finer grind extracts more.",
    });
    adjustments.push({
      lever: "temp", direction: "up",
      change: `Raise temp to ${previousBrew.temp_c + 2}°C`,
      reason: "Higher temp boosts extraction.",
    });
  }
  if (f.includes("bitter") || f.includes("over") || f.includes("astringent")) {
    adjustments.push({
      lever: "grind", direction: "coarser",
      change: "Move grind 1 click coarser",
      newSetting: Math.min(11, previousBrew.grind.setting + 1),
      reason: "Bitter/overextracted — coarser grind extracts less.",
    });
    adjustments.push({
      lever: "temp", direction: "down",
      change: `Lower temp to ${previousBrew.temp_c - 2}°C`,
      reason: "Lower temp eases extraction.",
    });
  }
  if (f.includes("weak") || f.includes("thin") || f.includes("watery")) {
    adjustments.push({
      lever: "ratio", direction: "stronger",
      change: "Try Bold profile next time",
      reason: "Tighter ratio = more concentration.",
    });
  }
  if (f.includes("muddy") || f.includes("sludgy") || f.includes("fines")) {
    adjustments.push({
      lever: "grind", direction: "coarser",
      change: "Move grind 1 click coarser",
      newSetting: Math.min(11, previousBrew.grind.setting + 1),
      reason: "Reduce fines for cleaner cup.",
    });
  }

  return adjustments;
}
